/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { featureFlags } from "@/config/feature-flag";
import { CONCILIATION_STORAGE_KEYS } from "@/constants/conciliation-storage";
import { StatusEnumStoreConciliation } from "@/enum/statusConciliation";
import {
  usePublicRecordEmailOpenedMutation,
  useRecordEmailOpenedMutation,
} from "@/services/conciliation/email-report";
import {
  useGetDivergenceQuery,
  usePublicGetDivergenceQuery,
  usePublicUpdateDivergenceStoreJustificationMutation,
  useUpdateDivergenceStoreJustificationMutation,
} from "@/services/divergence";
import {
  usePublicSendEmailFinalizeEvidenceMutation,
  useSendEmailFinalizeEvidenceMutation,
} from "@/services/email";
import { useOperatorMePublicQuery, useOperatorMeQuery } from "@/services/user";
import { mapApiErrorToUserMessage } from "@/utils/error-mapper";
import { handeOrderBy } from "@/utils/pagination";
import { toast } from "components/ui/use-toast";
import { useFilterState } from "../ducks/filter/hooks/filterState";

export function useEvidenceRegister() {
  const methods = useForm();
  const { control, getValues } = methods;
  const [isDisabledButton, setIsDisabledButton] = useState(true);
  const [hasReviewNotes, setHasReviewNotesState] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCheckingConciliation, setIsCheckingConciliation] = useState(true);
  const hasNavigatedRef = useRef(false);
  const hasRecordedEmailOpened = useRef(false);

  const useKeycloak = featureFlags.USE_KEYCLOAK;
  const navigate = useNavigate();

  const {
    data: storePrivate,
    isLoading: isLoadingStorePrivate,
    error: storeErrorPrivate,
  } = useOperatorMeQuery(undefined, { skip: useKeycloak });

  const {
    data: storePublic,
    isLoading: isLoadingStorePublic,
    error: storeErrorPublic,
  } = useOperatorMePublicQuery(undefined, { skip: !useKeycloak });

  const store = useKeycloak ? storePublic : storePrivate;
  const isLoadingStore = useKeycloak ? isLoadingStorePublic : isLoadingStorePrivate;
  const storeError = useKeycloak ? storeErrorPublic : storeErrorPrivate;

  const [sendEmailFinalizeEvidence, { isLoading: isLoadingFinalize }] = useKeycloak
    ? usePublicSendEmailFinalizeEvidenceMutation()
    : useSendEmailFinalizeEvidenceMutation();

  const [updateDivergenceStoreJustification] = useKeycloak
    ? usePublicUpdateDivergenceStoreJustificationMutation()
    : useUpdateDivergenceStoreJustificationMutation();

  const [recordEmailOpened] = useKeycloak
    ? usePublicRecordEmailOpenedMutation()
    : useRecordEmailOpenedMutation();

  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const orderBy = handeOrderBy(filters.order);

  const isRejected =
    localStorage.getItem(CONCILIATION_STORAGE_KEYS.REJECTED) === "true";
  const solicitationId =
    localStorage.getItem(CONCILIATION_STORAGE_KEYS.SOLICITATION_ID) ?? "";

  const storeData = store as any;
  const conciliationStoreId = storeData?.conciliation_store_id ?? "";
  const storeCode = storeData?.code ?? "";
  const conciliationDate = storeData?.conciliation_date;

  const divergenceQueryObject = useMemo(
    () => ({
      search: "",
      conciliation_store_id: conciliationStoreId,
      store_code: storeCode,
      justificationSearch: filters.searchJustification,
      solicitation_id: solicitationId,
      is_rejected: isRejected ? "true" : undefined,
      is_evidence: true,
      orderBy: JSON.stringify(orderBy),
      meta: { page: filters?.page ?? 1 },
    }),
    [conciliationStoreId, storeCode, filters.searchJustification, solicitationId, isRejected, orderBy, filters?.page],
  );

  const {
    data: response,
    isLoading: isLoadingDivergence,
    refetch,
    isFetching,
    error: divergenceError,
  } = useKeycloak
    ? usePublicGetDivergenceQuery(divergenceQueryObject)
    : useGetDivergenceQuery(divergenceQueryObject);

  const divergenceData = response?.data ?? [];
  const firstItem = divergenceData[0];
  const conciliation = firstItem?.conciliation;
  const conciliationStatus = conciliation?.status;
  const displayStoreCode = conciliation?.store?.code ?? "";

  useEffect(() => {
    if (hasRecordedEmailOpened.current) return;
    hasRecordedEmailOpened.current = true;

    const solicitationIdStr = localStorage.getItem("@email:solicitationId");
    const isRejectedEmail = localStorage.getItem("@verzel:rejected") === "true";

    if (!solicitationIdStr) {
      setIsCheckingConciliation(false);
      return;
    }

    recordEmailOpened({
      solicitation_id: parseInt(solicitationIdStr, 10),
      is_rejected: isRejectedEmail,
    })
      .unwrap()
      .then(() => setIsCheckingConciliation(false))
      .catch((error: any) => {
        if (error?.status === 403) {
          navigate("/informations?is-closed=true", { replace: true });
        } else {
          setIsCheckingConciliation(false);
        }
      });
  }, []);

  // Fallback: redirect if divergence query returns CLOSED
  useEffect(() => {
    if (!hasNavigatedRef.current && conciliationStatus === StatusEnumStoreConciliation.CLOSED) {
      hasNavigatedRef.current = true;
      navigate("/informations?is-closed=true", { replace: true });
    }
  }, [conciliationStatus, navigate]);

  // Track review notes
  useEffect(() => {
    if (divergenceData.length > 0) {
      const hasReview = divergenceData.some(
        (item: any) =>
          item?.review_info && Object.prototype.hasOwnProperty.call(item.review_info, "review_notes"),
      );
      setHasReviewNotesState(hasReview);
    }
  }, [divergenceData]);

  // Listen for justification changes from child components
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      setIsDisabledButton(event.detail.hasEmptyJustification);
    };
    window.addEventListener("justificationChange", handler as EventListener);
    return () => window.removeEventListener("justificationChange", handler as EventListener);
  }, []);

  // --- Actions ---
  function validateForm() {
    const data = getValues("divergence_opr");
    const hasEmpty = Object.entries(data).some(
      ([key, value]) => key.startsWith("store_justification_") && !value?.toString().trim(),
    );
    setIsDisabledButton(hasEmpty);
  }

  async function handleFinalizeEvidence() {
    setIsFinalizing(true);
    const formData = getValues("divergence_opr");

    try {
      const updatePromises = Object.keys(formData)
        .filter((key) => key.startsWith("store_justification_"))
        .map(async (key) => {
          const index = key.split("_").pop();
          await updateDivergenceStoreJustification({
            id: formData[`id_${index}`],
            store_justification: formData[`store_justification_${index}`],
            version: formData[`version_${index}`],
            conciliation_email_id: parseInt(solicitationId || "0"),
          }).unwrap();
        });

      await Promise.all(updatePromises);

      await sendEmailFinalizeEvidence({
        conciliation_id: conciliationStoreId?.toString() ?? "",
        store_code: displayStoreCode,
        solicitation_id: solicitationId,
      }).unwrap();

      navigate("/informations?success=true&type=evidence&clear-storage=true");
    } catch (e: any) {
      const apiMessage = e?.data?.messages?.[0] || e?.data?.message;
      const errorMessage = apiMessage || mapApiErrorToUserMessage(e, "evidence-register-finalize");
      toast({ variant: "destructive", description: errorMessage, duration: 5000 });
      setIsFinalizing(false);
    }
  }

  // --- Derived state ---
  const isLoading = isLoadingStore || isLoadingDivergence || isCheckingConciliation;
  const isSubmitting = isFinalizing || isLoadingFinalize;
  const isClosed = conciliationStatus === StatusEnumStoreConciliation.CLOSED;
  const queryError = storeError || divergenceError;

  const formattedConciliationDate = conciliationDate
    ? format(new Date(conciliationDate), "dd/MM/yyyy")
    : "";

  return {
    // form
    methods,
    control,
    validateForm,
    // data
    divergenceData,
    displayStoreCode,
    formattedConciliationDate,
    hasReviewNotes,
    // state
    isLoading,
    isFetching,
    isSubmitting,
    isDisabledButton,
    isClosed,
    queryError,
    // actions
    refetch,
    handleFinalizeEvidence,
  };
}
